/**
 * Oxbo-M client — enterprise master data (well register + catalogs).
 *
 * Read-through cache posture: Oxbo-Z consults & caches, never writes. Base URL
 * is VITE_OXBO_M_URL (m.dev.oxbo.energy by default). In dev / standalone mode
 * MSW intercepts these calls and serves mock register + catalog data, so the
 * app runs with no live backend.
 *
 * Endpoints mirror the live contract exactly:
 *   GET /v1/wells                              — list the register
 *   GET /v1/wells/{well_id}                    — one well
 *   GET /v1/categories/{key}/resolved          — an effective-dated catalog
 */
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  WellOut,
  ResolvedCatalogOut,
  WellView,
  CatalogResponse,
  WellUpsert,
  WellUpsertResult,
  CapabilitiesResponse,
  CapabilityDictEntry,
  CapabilityGrantBody,
} from "./types";
import { toWellView } from "./mappers";
import { getDevToken } from "./devAuth";
import { getProdToken } from "./prodAuth";
import { catalogCapabilities } from "./capabilities";
import type { UserRole } from "@app/slices/prefsSlice";

/**
 * The Bearer token to attach to Oxbo-M calls. Prefers the real-login prod token
 * (VITE_PROD_AUTH build); falls back to the dev-minted token (VITE_DEV_AUTH
 * build). In a mocks-only build both are null and no Authorization header is
 * sent (MSW answers the call anyway).
 */
function getAuthToken(): string | null {
  return getProdToken() ?? getDevToken();
}

/** Server-side filters the M contract accepts on /v1/wells. */
export interface WellListParams {
  status?: string;
  active_only?: boolean;
}

const M_BASE_URL = import.meta.env.VITE_OXBO_M_URL ?? "https://m.dev.oxbo.energy";

export const mApi = createApi({
  reducerPath: "mApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${M_BASE_URL}/v1`,
    // Inject the current JWT (real prod-login token, or dev-minted token). In a
    // mocks-only build both are null and no Authorization header is sent.
    prepareHeaders: (headers, { getState }) => {
      const token = getAuthToken();
      if (token) headers.set("Authorization", `Bearer ${token}`);
      // Forward the user's catalog-manage capabilities so Oxbo-M can decide
      // whether to include a catalog's `admin` block (§22c). This mirrors the
      // live contract where the token's claims carry these; against MSW the
      // handler reads them from X-Oxbo-Caps. A field user sends none → the
      // server omits the admin URL, so it never reaches the client.
      const role = (getState() as { prefs?: { role?: UserRole } }).prefs?.role;
      const caps = catalogCapabilities(role ?? "field");
      if (caps.length > 0) headers.set("X-Oxbo-Caps", caps.join(","));
      return headers;
    },
  }),
  // Enterprise master data changes rarely — keep it cached generously.
  keepUnusedDataFor: 300,
  tagTypes: ["Well", "Catalog", "Caps"],
  endpoints: (builder) => ({
    // Wells come back as raw WellOut[]; we normalize to WellView[] here so the
    // whole UI works in one clean, unit-aware shape.
    getWells: builder.query<WellView[], WellListParams | void>({
      // Default to active-only: the register also holds soft-deleted (is_active=
      // false) rows — e.g. old test wells — and the M route's own default is
      // active_only=false (returns ALL). The UI should only ever surface live
      // wells, so request active_only=true unless a caller explicitly overrides.
      query: (params) => ({ url: "/wells", params: params ?? { active_only: true } }),
      // Real backends can answer with an error/empty body that isn't the WellOut[]
      // the mock always returned; never assume `raw` is an array.
      transformResponse: (raw: WellOut[]) => (Array.isArray(raw) ? raw.map(toWellView) : []),
      providesTags: (result) =>
        result
          ? [
              ...result.map((w) => ({ type: "Well" as const, id: w.id })),
              { type: "Well" as const, id: "LIST" },
            ]
          : [{ type: "Well" as const, id: "LIST" }],
    }),
    getWell: builder.query<WellView, string>({
      query: (id) => `/wells/${id}`,
      transformResponse: (raw: WellOut) => toWellView(raw),
      providesTags: (_r, _e, id) => [{ type: "Well", id }],
    }),
    // Resolved catalog — e.g. activity codes, cost classes, vendors. Pickers
    // in the report screens read these. `asOf` supports effective-dating.
    getResolvedCatalog: builder.query<
      ResolvedCatalogOut,
      { categoryKey: string; asOf?: string; siteRegionKey?: string }
    >({
      query: ({ categoryKey, asOf, siteRegionKey }) => ({
        url: `/categories/${categoryKey}/resolved`,
        params: {
          ...(asOf ? { as_of: asOf } : {}),
          ...(siteRegionKey ? { site_region_key: siteRegionKey } : {}),
        },
      }),
      providesTags: (_r, _e, { categoryKey }) => [{ type: "Catalog", id: categoryKey }],
    }),

    /**
     * Catalog value-API (§22 / §23) — the picker-facing contract. Returns the
     * typeahead value list plus an OPTIONAL `admin` envelope that the server
     * omits for non-authorised callers (§22c). Supports server-side typeahead
     * (`q=`) + scope filtering for large catalogs (cost_accounts ~2,615) and
     * effective-dating (`as_of`).
     */
    getCatalog: builder.query<
      CatalogResponse,
      { key: string; q?: string; asOf?: string; scope?: string; active?: boolean }
    >({
      query: ({ key, q, asOf, scope, active }) => ({
        // Relative to the `${M_BASE_URL}/v1` baseUrl → GET /v1/catalogs/{key}
        // (the live M route). An absolute `${M_BASE_URL}/api/m/...` here would
        // stack on the baseUrl and hit a bogus /api/m/v1/api/m/... path → 404.
        url: `catalogs/${key}`,
        params: {
          ...(q ? { q } : {}),
          ...(asOf ? { as_of: asOf } : {}),
          ...(scope ? { scope } : {}),
          ...(active != null ? { active } : {}),
        },
      }),
      // A real/partial catalog body may omit `values` or send a non-array;
      // guarantee an array so every picker/label consumer can trust `values`.
      transformResponse: (raw: CatalogResponse): CatalogResponse => ({
        ...(raw ?? ({} as CatalogResponse)),
        values: Array.isArray(raw?.values) ? raw.values : [],
      }),
      providesTags: (_r, _e, { key }) => [{ type: "Catalog", id: `cat:${key}` }],
    }),

    /**
     * Upsert a well into the register — `PUT /v1/wells` (Oxbo-M). Idempotent on
     * (org, uwi): the same uwi updates its row rather than duplicating it, so
     * the "Crear Workover" wizard can create a brand-new asset OR re-point at an
     * existing one without risk of a duplicate ENO-8. Invalidates the register
     * LIST tag so `getWells` refetches and the new/updated well appears.
     */
    upsertWell: builder.mutation<WellUpsertResult, WellUpsert>({
      query: (body) => ({ url: "/wells", method: "PUT", body }),
      invalidatesTags: [{ type: "Well", id: "LIST" }],
    }),

    /**
     * The CALLER's resolved capability set — `GET /users/me/capabilities`
     * (Oxbo-M, relative to the `${M_BASE_URL}/v1` base). Returns the finite
     * capability strings the server resolved from role_assignment. The SPA reads
     * this via useCapabilities(); on error/loading it falls back to the legacy
     * role gate so nothing regresses (the server is the real gate regardless).
     * Returns just the `capabilities` array for the hook''s convenience.
     */
    getMyCapabilities: builder.query<string[], void>({
      query: () => "/users/me/capabilities",
      transformResponse: (raw: CapabilitiesResponse): string[] =>
        Array.isArray(raw?.capabilities) ? raw.capabilities : [],
      providesTags: [{ type: "Caps", id: "ME" }],
    }),

    /** The capability dictionary (key → label) for the ACL admin screen. */
    getCapabilityDict: builder.query<CapabilityDictEntry[], void>({
      query: () => "/capabilities",
      transformResponse: (raw: CapabilityDictEntry[]): CapabilityDictEntry[] =>
        Array.isArray(raw) ? raw : [],
    }),

    /**
     * Grant capabilities to a user in an org — `POST /users/{user_id}/capabilities`
     * (requires the caller to hold `acl:manage`; enforced server-side). Returns
     * the target''s resulting (merged) capability set. Invalidates the caller''s
     * own /me cache in case they granted to themselves.
     */
    grantCapabilities: builder.mutation<
      CapabilitiesResponse,
      { userId: string; body: CapabilityGrantBody }
    >({
      query: ({ userId, body }) => ({
        url: `/users/${userId}/capabilities`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Caps", id: "ME" }],
    }),

    /**
     * Revoke capabilities from a user — `DELETE /users/{user_id}/capabilities`.
     * A revoke that would strip the org''s last `acl:manage` holder is rejected
     * 409 by the server; a non-member target is rejected too. Returns the
     * target''s remaining capability set.
     */
    revokeCapabilities: builder.mutation<
      CapabilitiesResponse,
      { userId: string; body: CapabilityGrantBody }
    >({
      query: ({ userId, body }) => ({
        url: `/users/${userId}/capabilities`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: [{ type: "Caps", id: "ME" }],
    }),
  }),
});

export const {
  useGetWellsQuery,
  useGetWellQuery,
  useGetResolvedCatalogQuery,
  useGetCatalogQuery,
  useUpsertWellMutation,
  useGetMyCapabilitiesQuery,
  useGetCapabilityDictQuery,
  useGrantCapabilitiesMutation,
  useRevokeCapabilitiesMutation,
} = mApi;
